package com.steamstreet.vegasful.browser.account.subscriptions

import com.steamstreet.graphkt.client.GraphQLClientException
import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.browser.account.config
import com.steamstreet.vegasful.browser.account.safeShare
import com.steamstreet.vegasful.attribution.LinkCampaign
import com.steamstreet.vegasful.attribution.LinkMedium
import com.steamstreet.vegasful.attribution.LinkSource
import com.steamstreet.vegasful.attribution.LinkTags
import com.steamstreet.vegasful.attribution.newShareId
import com.steamstreet.vegasful.attribution.taggedUrl
import com.steamstreet.vegasful.browser.clientIconSvg
import com.steamstreet.vegasful.css.css
import com.steamstreet.vegasful.graphql.api.LikeInput
import com.steamstreet.vegasful.graphql.api.ShareInput
import com.steamstreet.vegasful.graphql.api.SubscriptionInput
import com.steamstreet.vegasful.web.ClientController
import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import com.steamstreet.vegasful.web.analytics.SaveGesture
import com.steamstreet.vegasful.web.analytics.entityIdOf
import com.steamstreet.vegasful.web.analytics.entityTypeOf
import com.steamstreet.vegasful.web.analytics.pageTypeOf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.css.Display
import kotlinx.css.display
import kotlinx.css.em
import kotlinx.css.fontSize
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.classes
import kotlinx.html.dom.append
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import kotlinx.html.unsafe
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventTarget
import kotlin.properties.Delegates.observable

val eventHandlerContext = CoroutineScope(Dispatchers.Default)
fun EventTarget.onClick(handler: suspend (Event) -> Unit) {
    addEventListener("click", {
        eventHandlerContext.launch {
            handler(it)
        }
    })
}

const val subscribeIcon = "interaction-heart"

class EntityInteractionsController(val element: Element, private val context: CoroutineScope) : ClientController {
    private val entity = element.getAttribute("data-entity-path")
    private val entityName = element.getAttribute("data-entity-name")

    private var isLiked: Boolean? by observable(null) { _, _, new ->
        context.launch {
            updateLikeButton(new)
        }
    }

    private fun appendButton(markerClass: String, iconId: String, onClick: suspend () -> Unit) {
        element.append {
            div {
                classes = setOf(markerClass)
                css {
                    display = Display.block
                    fontSize = 1.4.em
                }
                unsafe {
                    +clientIconSvg(iconId)
                }
                onClickFunction = {
                    context.launch {
                        onClick()
                    }
                }
            }
        }
    }

    /**
     * How this control presents itself, from `data-interactions`.
     *
     * `chip` is the preference picker: the element *is* the control, there are no buttons inside
     * it, and being liked is shown by the element's own styling rather than by a filled-in heart.
     * `like` is the ordinary control without the share button. Anything else — including the
     * attribute being absent — is the heart-and-share pair the entity pages have always had.
     */
    private val mode = element.getAttribute("data-interactions")

    private suspend fun updateLikeButton(subscribed: Boolean?) {
        if (subscribed == null) return

        if (mode == CHIP_MODE) {
            // The pill's own selected appearance, which `pillStyle` defines for every pill on the
            // site. Setting the attribute rather than a class of our own means a chosen preference
            // looks like anything else that is chosen, and keeps the styling in one place.
            if (subscribed) {
                element.setAttribute("data-selected", "1")
            } else {
                element.removeAttribute("data-selected")
            }
            return
        }

        val subscribedButton = element.querySelector(".$subscribeIcon")
        if (subscribedButton != null) {
            if (subscribed) {
                subscribedButton.addClass("liked")
            } else {
                subscribedButton.removeClass("liked")
            }
        }
    }

    private suspend fun unlike() {
        if (entity == null) return

        val oldSubscribed = isLiked
        isLiked = false
        try {
            GraphQL.mutation {
                subscriptions {
                    unsubscribe(SubscriptionInput(null, entity))
                }
            }
            reportPreference(liked = false)
        } catch (t: Throwable) {
            isLiked = oldSubscribed
        }
    }

    private suspend fun like() {
        if (entity == null) return

        if (!Account.isLoggedIn()) {
            // The page is on its way out; the like is re-attempted from `?subscribe=1` once the
            // visitor is back, and reported then. Reporting it here as well would count the one
            // intention twice.
            Account.login("${entity}?subscribe=1")
        }

        val oldLiked = isLiked
        isLiked = true
        try {
            GraphQL.mutation {
                subscriptions {
                    like(LikeInput(entity))
                }
            }
            reportPreference(liked = true)
        } catch (t: Throwable) {
            isLiked = oldLiked
        }
    }

    /**
     * Report a change of preference, reported only once the mutation has actually landed.
     *
     * The heart means two different things depending on what it is attached to, and the taxonomy
     * splits them: liking an *event* is a save, which is what `docs/retention.md` builds the
     * activation gate on, while liking a venue, tag or performer is a follow. Exactly one of the
     * two is sent — emitting both for one tap would inflate a launch gate that counts either.
     */
    private fun reportPreference(liked: Boolean) {
        val path = entity ?: return
        val type = entityTypeOf(path)
        val id = entityIdOf(path)
        if (type == "event") {
            if (liked) {
                Analytics.eventSaved(SaveGesture.LIKE, id, pageTypeOf(window.location.pathname))
            } else {
                Analytics.eventUnsaved(SaveGesture.LIKE, id)
            }
        } else {
            Analytics.track(
                if (liked) AnalyticsEvent.ENTITY_FOLLOWED else AnalyticsEvent.ENTITY_UNFOLLOWED,
                AnalyticsParam.ENTITY_TYPE to type,
                AnalyticsParam.ENTITY_ID to id,
                AnalyticsParam.ARRIVAL_SOURCE to pageTypeOf(window.location.pathname).takeIf { liked }
            )
        }
    }

    private suspend fun checkLikeStatus() {
        if (entity == null) return

        if (!Account.isLoggedIn()) {
            isLiked = false
            return
        }

        try {
            GraphQL.client {
                subscriptions {
                    mine {
                        interactionSummary(entity) {
                            this.liked
                        }
                    }
                }
            }.let {
                val loggedIn = it.subscriptions.mine != null
                val interactions = it.subscriptions.mine?.interactionSummary

                isLiked = interactions?.liked == true

                if (loggedIn && isLiked != true) {
                    val shouldSubscribe = document.location?.href?.substringAfter("?")?.split("&")?.map {
                        it.split("=").let {
                            if (it.size == 1) it.first() to it.first()
                            else it[0] to it[1]
                        }
                    }?.toMap()?.get("subscribe") == "1"

                    if (shouldSubscribe) {
                        isLiked = true // assume the subscription is going to work
                        like()
                    }
                }
            }
        } catch (ce: GraphQLClientException) {
            isLiked = false
        }
    }

    suspend fun share() {
        // Reported before the sheet opens, and for everybody. The Web Share API resolves when the
        // sheet is dismissed and rejects when it is cancelled, but not every browser distinguishes
        // the two, and on those that do not this would either under- or over-count. `share_completed`
        // on the web therefore means "the share control was used", which is what the control can
        // honestly observe.
        entity?.let {
            Analytics.track(
                AnalyticsEvent.SHARE_COMPLETED,
                AnalyticsParam.ENTITY_TYPE to entityTypeOf(it),
                AnalyticsParam.ENTITY_ID to entityIdOf(it),
                AnalyticsParam.ARRIVAL_SOURCE to pageTypeOf(window.location.pathname)
            )
        }

        // A fresh share id per use of the control, so a link that goes on to be forwarded stays
        // traceable to the share that started it. Nothing resolves the id back to this user yet;
        // that needs a share record written server-side, which inbound capture will want anyway.
        window.navigator.safeShare(
            url = taggedUrl(
                "https://${config.domain}${entity}",
                LinkTags(
                    source = LinkSource.WEB,
                    medium = LinkMedium.SHARE,
                    campaign = LinkCampaign.ENTITY_SHARE,
                    shareId = newShareId()
                )
            )
        )

        // Record the share as an interaction. The app already does this; without it, sharing from
        // the web contributes nothing to the user's recommendation profile.
        if (entity != null && Account.isLoggedIn()) {
            try {
                GraphQL.mutation {
                    subscriptions {
                        share(ShareInput(entity))
                    }
                }
            } catch (t: Throwable) {
                // Sharing succeeded even if recording it didn't, so there is nothing to report.
                console.warn("Unable to record share", t)
            }
        }
    }

    private suspend fun toggle() {
        if (isLiked == true) unlike() else like()
    }

    override suspend fun run() {
        if (mode == CHIP_MODE) {
            // The whole chip is the target. Nothing is appended: a picker asking "which of these
            // do you like" answers itself with the state of the thing you tapped, and a heart
            // inside each of twenty pills is a second thing to read on a screen that should be
            // scannable at a glance.
            element.onClick { toggle() }
        } else {
            appendButton(subscribeIcon, "heart") { toggle() }

            // `like` drops the share button, for places asking what somebody likes rather than
            // showing them a thing they might pass on. Absent the attribute the control is what it
            // has always been, so no existing caller changes.
            if (mode != LIKE_ONLY_MODE) {
                appendButton("interaction-share", "share") {
                    share()
                }
            }
        }
        checkLikeStatus()
    }

    companion object {
        const val CHIP_MODE = "chip"
        const val LIKE_ONLY_MODE = "like"
    }
}